/** import package [start] */
import { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { Inputbox } from '@zeeve-platform/inputbox-component';
/** import package [end] */

/** import custom dependency [start] */
import BaseUrls from '../../../utils/baseUrls';
/** import custom dependency [end] */

// types of node details
const nodeTypes = [
    {
        name: 'Full',
        code: 'NODE_FULL',
        disabled: false,
        description: 'Full node type for syncing chain of Shardeum.',
    },
    {
        name: 'Validator',
        code: 'NODE_VALIDATOR',
        disabled: false,
        description: 'Validator node type for syncing chain of Shardeum.',
    },
];

export interface IFRef {
    getNodeType: () => string;
    getNodeCode: () => string;
}

/** type declaration for the Node Type component props */
export interface NodeTypeProps {
    type: 'dedicated-node' | 'staking-node';
    nodeType: string;
    selectedCloud: string;
    setNodeType: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * Node Type component
 * @returns JSX.Element
 */
const NodeType = forwardRef<IFRef, NodeTypeProps>(({ type, nodeType, selectedCloud, setNodeType }, ref): JSX.Element => {
    //component dependent states
    const [nodeCode, setNodeCode] = useState<string>(nodeTypes[0].code);

    //exposed states and functions
    useImperativeHandle(ref, () => {
        return {
            getNodeType: () => nodeType,
            getNodeCode: () => nodeCode,
        };
    });

    /**
     * To get the node code from the config
     * @param code
     * @returns number
     */
    const getCodeFromConfig = (code: string) => BaseUrls.getItemCode(code);

    useEffect(() => {
        if (type === 'dedicated-node') setNodeCode(nodeTypes[0].code);
        else setNodeCode(nodeTypes[1].code);
    }, [type]);

    useEffect(() => {
        setNodeType(getCodeFromConfig(nodeCode));
    }, [nodeCode]);

    return (
        <>
            <label className='mt-4 fw-bold' style={{ fontWeight: 700 }}>
                {selectedCloud === 'ZEEVE_MANAGED' ? 'Node Type' : 'Select Node Type'}
            </label>
            <div className='row mt-3 mx-2'>
                {nodeCode === 'NODE_FULL' && (
                    <div className='form-check col-xl-2 col-lg-2 col-sm-2 col-md-2'>
                        <Inputbox
                            name='syncMode'
                            type='radio'
                            className='form-check-input'
                            id='dedicated-full'
                            disabled={nodeTypes[0].disabled || type !== 'dedicated-node'}
                            checked={nodeCode === 'NODE_FULL' ? 'checked' : ''}
                        />
                        <label
                            className='form-check-label mx-2 d-flex flex-row align-items-center'
                            htmlFor='dedicated-full'
                        >
                            {nodeTypes[0].name}
                            <i title={nodeTypes[0].description} className='mx-2 fa fa-info-circle' />
                        </label>
                    </div>
                )}
                {nodeCode === 'NODE_VALIDATOR' && (
                    <div className='form-check col-xl-2 col-lg-2 col-sm-2 col-md-2'>
                        <Inputbox
                            name='syncMode'
                            type='radio'
                            className='form-check-input'
                            id='staking-validator'
                            disabled={nodeTypes[1].disabled || type !== 'staking-node'}
                            checked={nodeCode === 'NODE_VALIDATOR' ? 'checked' : ''}
                        />
                        <label
                            className='form-check-label mx-2 d-flex flex-row align-items-center'
                            htmlFor='staking-validator'
                        >
                            {nodeTypes[1].name}
                            <i title={nodeTypes[1].description} className='mx-2 fa fa-info-circle' />
                        </label>
                    </div>
                )}
            </div>
        </>
    );
});

NodeType.displayName = 'NodeType';

export default NodeType;
